// Project (delivery) detail — overview + satisfaction + milestones tracker.
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Trash2 } from "lucide-react";

const PROJECT_STATUSES = ["PLANNING", "ACTIVE", "ON_HOLD", "COMPLETED", "CANCELLED"] as const;
const MILESTONE_STATUSES = ["NOT_STARTED", "IN_PROGRESS", "COMPLETED", "AT_RISK", "OVERDUE"] as const;

const apiErr = (e: any) => e?.response?.data?.message ?? e?.response?.data?.error?.message ?? e?.message ?? "Something went wrong";
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : null);
const lbl = (s: string) => s.replace(/_/g, " ");

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ["/projects", id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: async () => (await api.get(`/projects/${id}`)).data, enabled: !!id });
  const users = useQuery({ queryKey: ["/users", "picker"], queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string }[] });
  const refetch = () => qc.invalidateQueries({ queryKey });
  async function patch(body: Record<string, unknown>) { await api.put(`/projects/${id}`, body); refetch(); }
  async function del() {
    if (!data || !confirm(`Delete project "${data.name}"?`)) return;
    await api.delete(`/projects/${id}`);
    qc.invalidateQueries({ queryKey: ["/projects"] });
    navigate("/projects");
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const p = data;
  const ms: any[] = p.milestones ?? [];
  const done = ms.filter((m) => m.status === "COMPLETED").length;
  const avgPct = ms.length ? Math.round(ms.reduce((a, m) => a + (m.pctComplete ?? 0), 0) / ms.length) : 0;

  return (
    <>
      <PageHeader title={p.name} description={lbl(p.status)} actions={<Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>} />
      <div className="space-y-4 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-base">Overview</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Status</span>
                <Select value={p.status} onChange={(e) => patch({ status: e.target.value })} className="h-7 w-40 text-xs">{PROJECT_STATUSES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}</Select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">PM</span>
                <Select value={p.pmId ?? ""} onChange={(e) => patch({ pmId: e.target.value || null })} className="h-7 w-40 text-xs">
                  <option value="">—</option>
                  {users.data?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
              </div>
              <KV k="Kickoff" v={fmtDate(p.kickoffDate)} />
              <KV k="Deal" v={p.deal ? <Link className="hover:underline" to={`/deals/${p.deal.id}`}>{p.deal.name}</Link> : null} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Client satisfaction</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="csat" className="text-xs text-muted-foreground">CSAT (1–10)</Label>
                <Input id="csat" type="number" min={1} max={10} defaultValue={p.csatScore ?? ""} className="h-7 w-24"
                  onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (p.csatScore ?? null)) patch({ csatScore: v }); }} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="nps" className="text-xs text-muted-foreground">NPS (−100…100)</Label>
                <Input id="nps" type="number" min={-100} max={100} defaultValue={p.npsScore ?? ""} className="h-7 w-24"
                  onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (p.npsScore ?? null)) patch({ npsScore: v }); }} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Progress</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{done}/{ms.length}</div>
              <div className="text-xs text-muted-foreground">milestones complete</div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded bg-muted">
                <div className="h-2 bg-primary" style={{ width: `${avgPct}%` }} />
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{avgPct}% avg completion</div>
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader><CardTitle className="text-base">Milestones</CardTitle></CardHeader>
          <CardContent>
            <MilestonesPanel projectId={p.id} milestones={ms} users={users.data ?? []} onChange={refetch} />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function MilestonesPanel({ projectId, milestones, users, onChange }: { projectId: string; milestones: any[]; users: { id: string; name: string }[]; onChange: () => void }) {
  const [addOpen, setAddOpen] = useState(false);
  const userName = (uid?: string) => users.find((u) => u.id === uid)?.name;
  async function setField(mid: string, body: Record<string, unknown>) { await api.put(`/projects/milestones/${mid}`, body); onChange(); }
  async function remove(mid: string) { if (!confirm("Delete this milestone?")) return; await api.delete(`/projects/milestones/${mid}`); onChange(); }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{milestones.length} milestone{milestones.length === 1 ? "" : "s"}</div>
        <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4" />Add milestone</Button>
      </div>
      {milestones.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No milestones yet.</p>}
      {milestones.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead><Tr><Th>Milestone</Th><Th>Owner</Th><Th>Status</Th><Th>% done</Th><Th>Due</Th><Th className="w-10"></Th></Tr></Thead>
            <Tbody>
              {milestones.map((m: any) => (
                <Tr key={m.id} className="group">
                  <Td className="font-medium">{m.name}</Td>
                  <Td className="text-sm text-muted-foreground">{m.owner?.name ?? userName(m.ownerId) ?? "—"}</Td>
                  <Td><Select value={m.status} onChange={(e) => setField(m.id, { status: e.target.value })} className="h-7 w-36 text-xs">{MILESTONE_STATUSES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}</Select></Td>
                  <Td>
                    <Input type="number" min={0} max={100} defaultValue={m.pctComplete ?? 0} className="h-7 w-16"
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== (m.pctComplete ?? 0)) setField(m.id, { pctComplete: v }); }} />
                  </Td>
                  <Td className="text-sm">{fmtDate(m.dueDate) ?? "—"}</Td>
                  <Td><button onClick={() => remove(m.id)} className="text-destructive opacity-0 group-hover:opacity-100" aria-label="Remove"><Trash2 className="h-4 w-4" /></button></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      {addOpen && <AddMilestoneModal projectId={projectId} users={users} onClose={() => setAddOpen(false)} onAdded={onChange} />}
    </div>
  );
}

function AddMilestoneModal({ projectId, users, onClose, onAdded }: { projectId: string; users: { id: string; name: string }[]; onClose: () => void; onAdded: () => void }) {
  const [name, setName] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("NOT_STARTED");
  const [pctComplete, setPctComplete] = useState("0");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => { await api.post(`/projects/${projectId}/milestones`, { name, ownerId: ownerId || undefined, dueDate: dueDate || undefined, status, pctComplete: Number(pctComplete) }); },
    onSuccess: () => { onAdded(); onClose(); },
    onError: (e) => setErr(apiErr(e)),
  });
  return (
    <BulkModal open onClose={onClose} title="Add milestone" confirmLabel="Add" confirmDisabled={!name.trim()} busy={mut.isPending} error={err} onConfirm={() => mut.mutate()}>
      <div className="space-y-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Phase 1 sign-off" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Owner</Label><Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}><option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Select></div>
        <div className="space-y-1"><Label>Status</Label><Select value={status} onChange={(e) => setStatus(e.target.value)}>{MILESTONE_STATUSES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}</Select></div>
        <div className="space-y-1"><Label>Due date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
        <div className="space-y-1"><Label>% complete</Label><Input type="number" min={0} max={100} value={pctComplete} onChange={(e) => setPctComplete(e.target.value)} /></div>
      </div>
    </BulkModal>
  );
}
